import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Plus, X, Loader2, Heart } from "lucide-react";
import { toast } from "sonner";

/**
 * Inline member-submitted attributes for a single angel.
 * - Lists approved attributes with upvote counts.
 * - "Suggest an attribute" button reveals a small inline form (60 char max).
 * - Tap an attribute to upvote ("I felt this too"); tap again to remove.
 * - Hidden on unavailable angels.
 */
export default function AngelAttributes({ angelNumber, hidden = false }) {
    const [items, setItems] = useState([]);
    const [hasPending, setHasPending] = useState(false);
    const [loading, setLoading] = useState(true);
    const [formOpen, setFormOpen] = useState(false);
    const [text, setText] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get(`/angels/${angelNumber}/attributes`);
            setItems(data?.items || []);
            setHasPending(Boolean(data?.hasPending));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        if (!hidden) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [angelNumber, hidden]);

    if (hidden) return null;

    const submit = async (e) => {
        e?.preventDefault();
        const trimmed = text.trim();
        if (trimmed.length < 2) return;
        setSubmitting(true);
        try {
            await api.post(`/angels/${angelNumber}/attributes`, { text: trimmed });
            toast.success("Thank you — your attribute is awaiting the curator's blessing.");
            setText("");
            setFormOpen(false);
            setHasPending(true);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg =
                typeof detail === "object" ? detail.message :
                typeof detail === "string" ? detail :
                "Could not submit. Try again in a moment.";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const toggleUpvote = async (id) => {
        // Optimistic flip
        setItems((prev) =>
            prev.map((a) =>
                a.id === id
                    ? { ...a, iUpvoted: !a.iUpvoted, upvotes: a.upvotes + (a.iUpvoted ? -1 : 1) }
                    : a,
            ),
        );
        try {
            await api.post(`/angels/${angelNumber}/attributes/${id}/upvote`);
        } catch {
            // Roll back on failure
            load();
            toast.error("Could not record your upvote.");
        }
    };

    return (
        <section
            className="mt-10 border-t border-white/5 pt-8"
            data-testid="angel-attributes"
        >
            <span className="font-label text-[10px] tracking-[0.3em] uppercase text-[#cfa052]">
                Attributes
            </span>

            {loading ? (
                <p className="mt-3 font-poetic text-sm text-[#6e6a62]">listening…</p>
            ) : items.length === 0 ? (
                <p className="mt-3 font-poetic text-sm text-[#6e6a62]" data-testid="angel-attributes-empty">
                    Be the first to suggest one.
                </p>
            ) : (
                <ul className="mt-4 flex flex-wrap gap-2" data-testid="angel-attributes-list">
                    {items.map((a) => (
                        <li key={a.id}>
                            {a.isMine ? (
                                <span
                                    data-testid={`angel-attribute-${a.id}`}
                                    title="Your suggestion — others can upvote it."
                                    className="inline-flex items-center gap-2 border border-[#cfa052]/30 bg-[#cfa052]/[0.06] rounded-sm px-3 py-1.5 font-poetic text-sm text-[#e8e4db]"
                                >
                                    <span>{a.text}</span>
                                    <span className="inline-flex items-center gap-1 font-label text-[10px] tracking-widest uppercase text-[#cfa052]">
                                        <Heart className="w-3 h-3" />
                                        {a.upvotes}
                                    </span>
                                    <span className="font-label text-[9px] tracking-[0.25em] uppercase text-[#cfa052]/70">
                                        yours
                                    </span>
                                </span>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => toggleUpvote(a.id)}
                                    data-testid={`angel-attribute-${a.id}`}
                                    className={`group inline-flex items-center gap-2 border rounded-sm px-3 py-1.5 font-poetic text-sm transition-colors ${
                                        a.iUpvoted
                                            ? "border-[#cfa052]/60 bg-[#cfa052]/10 text-[#e8e4db]"
                                            : "border-white/10 text-[#9a958a] hover:border-[#cfa052]/40 hover:text-[#e8e4db]"
                                    }`}
                                >
                                    <span>{a.text}</span>
                                    <span className="inline-flex items-center gap-1 font-label text-[10px] tracking-widest uppercase text-[#6e6a62] group-hover:text-[#cfa052]">
                                        <Heart
                                            className={`w-3 h-3 ${a.iUpvoted ? "fill-[#cfa052] text-[#cfa052]" : ""}`}
                                        />
                                        {a.upvotes}
                                    </span>
                                </button>
                            )}
                        </li>
                    ))}
                </ul>
            )}

            <div className="mt-5">
                {hasPending ? (
                    <p
                        className="font-poetic text-xs text-[#6e6a62] italic"
                        data-testid="angel-attribute-pending-hint"
                    >
                        Your suggestion is awaiting the curator&apos;s blessing.
                    </p>
                ) : !formOpen ? (
                    <button
                        type="button"
                        onClick={() => setFormOpen(true)}
                        data-testid="angel-attribute-open-form"
                        className="inline-flex items-center gap-2 font-label text-[10px] tracking-[0.25em] uppercase text-[#cfa052] hover:text-[#e0b569] transition-colors"
                    >
                        <Plus className="w-3 h-3" />
                        Suggest an attribute
                    </button>
                ) : (
                    <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2 max-w-md" data-testid="angel-attribute-form">
                        <input
                            type="text"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            maxLength={60}
                            placeholder="e.g. Sudden delight"
                            autoFocus
                            data-testid="angel-attribute-input"
                            className="flex-1 bg-[#151311] border border-white/10 focus:border-[#cfa052]/60 outline-none rounded-sm px-3 py-2 font-poetic text-sm text-[#e8e4db] placeholder:text-[#4a4640]"
                        />
                        <div className="flex gap-2">
                            <button
                                type="submit"
                                disabled={submitting || text.trim().length < 2}
                                data-testid="angel-attribute-submit"
                                className="inline-flex items-center gap-2 bg-[#cfa052] hover:bg-[#e0b569] text-[#0c0b0a] px-4 py-2 font-label text-[10px] tracking-[0.25em] uppercase rounded-sm transition-colors disabled:opacity-50"
                            >
                                {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                                Submit
                            </button>
                            <button
                                type="button"
                                onClick={() => { setFormOpen(false); setText(""); }}
                                data-testid="angel-attribute-cancel"
                                className="p-2 text-[#6e6a62] hover:text-[#e8e4db] transition-colors"
                                aria-label="Cancel"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </section>
    );
}
